import { useCallback, useEffect, useState } from 'react';

import '../../../../../../paymentDebitCard/LinkDebitCard.module.css';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    fetchGetPaymentMethod,
    fetchPostRepaymentRegisterRepaymentMethod
} from '@iwoca/lapi-client/edge';
import { Button, Message, Modal } from '@iwoca/orion';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter, maintainStateKey } from '@iwRouter/LocationHelper';
import { useQueryClient } from '@tanstack/react-query';
import { CardType, Frames } from 'frames-react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import { useStateKey } from '../../../../../../../iwQueryClient/useStateKey';
import { PaymentMethodAuthenticationModal } from '../../../../../../modals/paymentMethodAuthenticationModal/PaymentMethodAuthenticationModal';
import { BillingPostcodeField } from '../../../../../../paymentDebitCard/CheckoutLinkDebitCard/formFields/BillingPostcodeField';
import { CardholderNameField } from '../../../../../../paymentDebitCard/CheckoutLinkDebitCard/formFields/CardholderNameField';
import { CardNumberField } from '../../../../../../paymentDebitCard/CheckoutLinkDebitCard/formFields/CardNumberField';
import { CVVField } from '../../../../../../paymentDebitCard/CheckoutLinkDebitCard/formFields/CVVField';
import { ExpiryDateField } from '../../../../../../paymentDebitCard/CheckoutLinkDebitCard/formFields/ExpiryDateField';
import { getFramesConfig } from '../../../../../../paymentDebitCard/CheckoutLinkDebitCard/frames.config';
import { useRequirementsProgress } from '../../../useRequirementsProgress';

const FUNDING_REQUIREMENTS_LIST_PATH = '/funding-requirements/';
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 10;

type TFormValues = { billingPostcode: string; cardholderName: string };

function wait(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export const CheckoutDebitCardForm = () => {
    const intl = useIntl();
    const history = useHistory();
    const { search } = useLocation();
    const stateKey = useStateKey();
    const queryClient = useQueryClient();
    const progress = useRequirementsProgress();
    const location = getCurrentLocation();
    const offerId = getQueryParameter(location, 'offerId');
    const amount = getQueryParameter(location, 'amount');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [authenticationFailed, setAuthenticationFailed] = useState(false);
    const [is3DSModalOpen, setIs3DSModalOpen] = useState(false);
    const [checkout3DSecureRedirectUrl, setCheckout3DSecureRedirectUrl] =
        useState<string | null>(null);
    const [paymentMethodCountBefore, setPaymentMethodCountBefore] = useState(0);

    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
        reset: resetForm
    } = useForm<TFormValues>({
        defaultValues: { billingPostcode: '', cardholderName: '' }
    });

    const close3DSModal = useCallback(() => {
        setIs3DSModalOpen(false);
        setIsSubmitting(false);
        Frames.init();
        resetForm();
    }, [resetForm]);

    const navigateToList = useCallback(() => {
        history.push(
            maintainStateKey({
                pathname: FUNDING_REQUIREMENTS_LIST_PATH,
                search
            })
        );
    }, [history, search]);

    const pollAndComplete = useCallback(
        async (previousCount: number) => {
            for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
                await wait(POLL_INTERVAL_MS);
                const { data } = await fetchGetPaymentMethod({ stateKey });
                if (data.length > previousCount) {
                    await queryClient.invalidateQueries({
                        queryKey: [
                            'fetchGetFundingRequirementsCreditFacility',
                            stateKey,
                            offerId,
                            amount
                        ]
                    });
                    queryClient.removeQueries({
                        queryKey: ['fetchGetPaymentMethod', stateKey]
                    });

                    tracking.ampli.fundingRequirementsDebitCardCompleted({
                        version: '2.0'
                    });
                    tracking.ampli.fundingRequirementCompleted({
                        requirement: 'debit_card',
                        progress: { ...progress, debit_card: true },
                        version: '2.0'
                    });

                    navigateToList();
                    return;
                }
            }

            setAuthenticationFailed(true);
            setIsSubmitting(false);
        },
        [stateKey, queryClient, offerId, amount, progress, navigateToList]
    );

    useEffect(() => {
        const handle3DSCompleteEvent = (event: MessageEvent) => {
            if (event.data === '3DS-authentication-complete') {
                setIs3DSModalOpen(false);
                pollAndComplete(paymentMethodCountBefore);
            } else if (event.data === '3DS-authentication-failed') {
                setIs3DSModalOpen(false);
                setAuthenticationFailed(true);
                setIsSubmitting(false);
            }
        };

        window.addEventListener('message', handle3DSCompleteEvent);
        return () =>
            window.removeEventListener('message', handle3DSCompleteEvent);
    }, [pollAndComplete, paymentMethodCountBefore]);

    const onSubmit = async () => {
        setAuthenticationFailed(false);
        setIsSubmitting(true);

        let previousCount = 0;
        try {
            const { data: paymentMethods } = await fetchGetPaymentMethod({
                stateKey
            });
            previousCount = paymentMethods.length;
            setPaymentMethodCountBefore(previousCount);

            const { token, card_type } = await Frames.submitCard();
            const response = await fetchPostRepaymentRegisterRepaymentMethod({
                stateKey,
                body: {
                    data: {
                        token,
                        card_type: card_type as Uppercase<CardType>
                    }
                }
            });

            const redirect_url = response.data.checkout_3ds_redirect_link;

            if (redirect_url) {
                setCheckout3DSecureRedirectUrl(redirect_url);
                setIs3DSModalOpen(true);
                return;
            }
        } catch {
            setAuthenticationFailed(true);
            setIsSubmitting(false);
            Frames.init();
            resetForm();
            return;
        }

        Frames.init();
        resetForm();
        await pollAndComplete(previousCount);
    };

    return (
        <>
            {checkout3DSecureRedirectUrl && (
                <Modal isOpen={is3DSModalOpen} onClose={close3DSModal}>
                    <PaymentMethodAuthenticationModal
                        authenticationIframeURL={checkout3DSecureRedirectUrl}
                    />
                </Modal>
            )}
            {authenticationFailed && (
                <Message
                    className="mb-l"
                    type="error"
                    message={intl.formatMessage({
                        id: '_.funding_requirements.debit_card_step.registration_failed',
                        defaultMessage:
                            'Failed to register the card. Please try again.'
                    })}
                />
            )}
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmit)();
                }}
                className="[&_div]:max-w-none"
            >
                <Frames
                    config={getFramesConfig({
                        billingPostcode: getValues('billingPostcode'),
                        cardholderName: getValues('cardholderName')
                    })}
                >
                    <CardholderNameField
                        formRegister={register}
                        error={errors.cardholderName}
                        placeholder="Name on card"
                    />
                    <CardNumberField />
                    <div className="flex gap-l">
                        <div className="flex-1">
                            <ExpiryDateField />
                        </div>
                        <div className="flex-1">
                            <CVVField
                                label={
                                    <FormattedMessage
                                        id="_.funding_requirements.debit_card_step.security_code_label"
                                        defaultMessage="Security Code"
                                    />
                                }
                            />
                        </div>
                    </div>
                    <BillingPostcodeField
                        formRegister={register}
                        error={errors.billingPostcode}
                        placeholder="SW1A 2AA"
                    />
                </Frames>

                <Button
                    variant="primary-rounded"
                    type="submit"
                    className="mt-l"
                    loading={isSubmitting}
                >
                    <FormattedMessage
                        id="_.funding_requirements.debit_card_step.confirm"
                        defaultMessage="Confirm card details"
                    />
                </Button>
            </form>
        </>
    );
};
